import { useRouter } from '@tanstack/react-router';
import { type CSSProperties, useEffect, useRef, useState } from 'react';

const ANNOUNCER_STYLE = {
  position: 'absolute',
  top: 0,
  width: 1,
  height: 1,
  padding: 0,
  overflow: 'hidden',
  clip: 'rect(0, 0, 0, 0)',
  whiteSpace: 'nowrap',
  border: 0,
} satisfies CSSProperties;

function isInIframe(): boolean {
  try {
    return globalThis.self !== globalThis.top;
  } catch {
    return true;
  }
}

function getRouteAnnouncement(): string {
  const pageHeading = document.querySelector('h1');
  if (pageHeading?.textContent) return pageHeading.textContent;
  if (document.title) return document.title;
  return `new page at ${globalThis.location.pathname}`;
}

function resetRouteFocus(): void {
  document.body.setAttribute('tabindex', '-1');
  document.body.focus();
  document.body.removeAttribute('tabindex');
}

export function RouteRenderAnnouncer({ observeNavigation }: Readonly<{ observeNavigation: boolean }>) {
  const announcementRef = useRef<HTMLDivElement>(null);
  const router = useRouter();
  const [announcedLocation, setAnnouncedLocation] = useState<Readonly<{ publicHref: string }>>(() => ({
    publicHref: router.state.location.publicHref,
  }));
  const handledHrefRef = useRef(announcedLocation.publicHref);

  useEffect(() => {
    if (!observeNavigation) return;
    return router.subscribe('onRendered', ({ fromLocation, toLocation }) => {
      if (fromLocation?.publicHref !== handledHrefRef.current) return;
      handledHrefRef.current = toLocation.publicHref;
      setAnnouncedLocation({ publicHref: toLocation.publicHref });
    });
  }, [observeNavigation, router]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: each accepted render schedules a fresh announcement and focus cycle
  useEffect(() => {
    if (isInIframe()) return;

    if (announcementRef.current !== null) announcementRef.current.innerText = getRouteAnnouncement();
    resetRouteFocus();
  }, [announcedLocation]);

  return (
    <div
      id="route-announcer"
      style={ANNOUNCER_STYLE}
      role="alert"
      aria-live="assertive"
      aria-atomic="true"
      ref={announcementRef}
    />
  );
}
